import { motion } from 'framer-motion';
import { CheckCircle, QrCode, ShieldCheck, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { trackCTA } from '../utils/analytics';

const structuredData = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'IDGate — Co-working Space Visitor Management',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'INR', description: 'Free pilot' },
  description:
    'Replace paper visitor registers at co-working spaces with Aadhaar-verified digital check-in. DPDP compliant, enterprise-ready.',
  provider: { '@type': 'Organization', name: 'IDGate', url: 'https://idgate.in' },
};

const faqData = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Can IDGate handle both member and visitor check-in at a co-working space?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. IDGate supports separate QR flows — one for registered members and one for walk-in visitors. Each generates an Aadhaar-verified record with a timestamp and consent trail.',
      },
    },
    {
      '@type': 'Question',
      name: 'How does IDGate help with DPDP compliance for co-working spaces?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "IDGate captures explicit digital consent from each visitor before verification. Aadhaar data is verified by UIDAI and not stored by IDGate. You receive only the record you need, satisfying India's Digital Personal Data Protection Act requirements.",
      },
    },
    {
      '@type': 'Question',
      name: 'Can enterprise clients see that our co-working space uses verified check-in?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. IDGate can generate a visitor log that enterprise clients can audit, showing verified entry records for their employees at your space.',
      },
    },
  ],
};

const PAIN_POINTS = [
  'Who else has access to this floor?',
  'Do you have a record of everyone who came in last Tuesday?',
  "What happens if an unverified visitor accesses our team's desk?",
  'Is your visitor data DPDP compliant?',
  'Can you show me the entry log for our employees this month?',
  'We had a security incident — can you tell us who was in the building?',
];

const STEPS = [
  {
    icon: QrCode,
    title: 'Visitor scans QR at entry',
    body: 'One QR code at your entrance or reception. Works on any smartphone with mAadhaar installed.',
  },
  {
    icon: ShieldCheck,
    title: 'Identity verified instantly',
    body: 'UIDAI confirms identity in under 60 seconds. No paper. No manual check. No photocopies.',
  },
  {
    icon: Users,
    title: 'Record synced to your system',
    body: 'Verified visitor log lands in your Google Sheet or CRM via webhook. Real-time and exportable.',
  },
];

const BENEFITS = [
  'Aadhaar-verified entry for every visitor and member',
  'Enterprise-grade security posture for your pitch deck',
  'DPDP compliant — consent captured at every check-in',
  'Exportable visitor log for client audits',
  'Separate flows for members vs walk-in visitors',
  'Google Sheet integration — no new dashboard',
  'Community manager does not touch visitor IDs',
  'Free 30-day pilot — no contract, no card',
];

const CoWorking = () => (
  <>
    <SEOHead
      title="Aadhaar Visitor Management for Co-working Spaces"
      description="Replace paper visitor registers at your co-working space with Aadhaar-verified digital check-in. DPDP compliant, enterprise-ready. Free pilot for independent co-working spaces in India."
      keywords="co-working visitor management India, visitor check-in co-working space, Aadhaar verification co-working, DPDP compliant visitor log, digital visitor register India, co-working security India"
      url="https://idgate.in/co-working"
      structuredData={[structuredData, faqData]}
    />

    {/* Hero */}
    <section className="border-b border-slate-200 bg-white">
      <div className="container-custom py-20 lg:py-28">
        <div className="mx-auto max-w-3xl text-center">
          <p className="eyebrow">For Co-working Spaces</p>
          <h1 className="section-title mt-3">
            Your visitor register is a liability.
            <br className="hidden sm:block" /> Make it a feature.
          </h1>
          <p className="mt-6 text-lg text-slate-600">
            Enterprise clients want to know who enters your space. IDGate gives you Aadhaar-verified
            check-in that makes your co-working space the most trusted address on the block.
          </p>
          <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <Link
              to="/contact"
              className="btn-primary"
              onClick={() => trackCTA('start_pilot_coworking', 'coworking_hero')}
            >
              Start free pilot
            </Link>
            <Link
              to="/demo"
              className="btn-secondary"
              onClick={() => trackCTA('view_demo_coworking', 'coworking_hero')}
            >
              See how it works
            </Link>
          </div>
        </div>
      </div>
    </section>

    {/* Pain points */}
    <section className="bg-slate-50 py-16">
      <div className="container-custom">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="section-title">What enterprise clients ask when they visit</h2>
          <p className="mt-4 text-slate-600">These are the questions that lose you the account.</p>
        </div>
        <div className="mx-auto mt-12 grid max-w-4xl gap-6 sm:grid-cols-2">
          {PAIN_POINTS.map(q => (
            <div key={q} className="rounded-xl border border-amber-100 bg-white p-5 shadow-sm">
              <p className="text-sm italic text-slate-700">&ldquo;{q}&rdquo;</p>
            </div>
          ))}
        </div>
        <p className="mt-8 text-center text-sm text-slate-500">
          With IDGate, the answer to every one of these is:{' '}
          <strong className="text-slate-700">&ldquo;Yes, let me pull that now.&rdquo;</strong>
        </p>
      </div>
    </section>

    {/* How it works */}
    <section className="bg-white py-16">
      <div className="container-custom">
        <h2 className="section-title text-center">How IDGate works at your entrance</h2>
        <div className="mx-auto mt-12 grid max-w-4xl gap-8 sm:grid-cols-3">
          {STEPS.map((step, i) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="rounded-2xl border border-slate-200 bg-slate-50 p-6"
            >
              <step.icon className="h-8 w-8 text-primary-600" aria-hidden />
              <h3 className="mt-4 font-semibold text-slate-900">{step.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{step.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* Benefits */}
    <section className="bg-primary-700 py-16 text-white">
      <div className="container-custom">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-2xl font-bold tracking-tight">What IDGate adds to your space</h2>
          <ul className="mt-8 grid gap-4 sm:grid-cols-2">
            {BENEFITS.map(benefit => (
              <li key={benefit} className="flex items-start gap-3">
                <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-accent-400" aria-hidden />
                <span className="text-sm text-primary-100">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section className="bg-white py-16">
      <div className="container-custom">
        <h2 className="section-title text-center">Common questions from co-working operators</h2>
        <div className="mx-auto mt-10 max-w-2xl divide-y divide-slate-200">
          {faqData.mainEntity.map(faq => (
            <details key={faq.name} className="group py-5">
              <summary className="flex cursor-pointer items-center justify-between font-medium text-slate-900 marker:content-none">
                {faq.name}
                <span className="ml-4 flex-shrink-0 text-slate-400 transition-transform group-open:rotate-180">
                  &#9662;
                </span>
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{faq.acceptedAnswer.text}</p>
            </details>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="border-t border-slate-200 bg-slate-50 py-16">
      <div className="container-custom text-center">
        <h2 className="section-title">Make your space enterprise-ready this week.</h2>
        <p className="mt-4 text-slate-600">Free 30-day pilot. One afternoon to set up. No contract.</p>
        <Link
          to="/contact"
          className="btn-primary mt-8 inline-block"
          onClick={() => trackCTA('start_pilot_coworking', 'coworking_footer_cta')}
        >
          Start free pilot for your space
        </Link>
      </div>
    </section>
  </>
);

export default CoWorking;
